import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper as MuiPaper,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ScenarioComparisonSeries,
  ScenarioCurveType,
  scenariosApi,
} from '@/api/scenarios';
import { ScenarioResponse } from '@/types/scenario';
import {
  convertComparisonOilLiquid,
  convertOilVolumeForDisplay,
  formatThousandVolumeTooltip,
  getComparisonDifferenceAxisLabel,
  getComparisonProductionAxisLabel,
  SCENARIO_UNIT_BARRELS,
  SCENARIO_UNIT_TONS,
} from '@/utils/scenarioUnits';

type ScenarioStatusInfo = {
  is_up_to_date: boolean;
  reason: string | null;
};

type LayerComparisonSection = {
  id: string;
  scenario1LayerId: string;
  scenario2LayerId: string;
};

const renderResponsiveLegend = (props: any) => {
  const payload = props?.payload ?? [];
  if (!payload.length) return null;

  return (
    <Box
      sx={{
        width: '100%',
        px: 1,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        columnGap: 2,
        rowGap: 0.5,
      }}
    >
      {payload.map((entry: any) => (
        <Box
          key={entry.value}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: entry.color,
              mr: 0.75,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const ScenarioComparisonMockPage: React.FC = () => {
  const CHART_HEIGHT = 360;
  const CHART_CARD_HEIGHT = CHART_HEIGHT + 150;
  const [scenarios, setScenarios] = useState<ScenarioResponse[]>([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [scenariosError, setScenariosError] = useState<string | null>(null);

  const [scenarioAId, setScenarioAId] = useState<string>('');
  const [scenarioBId, setScenarioBId] = useState<string>('');
  const [curveType, setCurveType] = useState<ScenarioCurveType>('ml');

  const [statusByScenarioId, setStatusByScenarioId] = useState<Record<string, ScenarioStatusInfo>>({});

  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [comparisonSeries, setComparisonSeries] = useState<ScenarioComparisonSeries[] | null>(null);
  const [layerComparisonSections, setLayerComparisonSections] = useState<LayerComparisonSection[]>([]);
  const [displayUnitId, setDisplayUnitId] = useState<number>(SCENARIO_UNIT_TONS);

  const productionAxisLabel = getComparisonProductionAxisLabel(displayUnitId);
  const differenceAxisLabel = getComparisonDifferenceAxisLabel(displayUnitId);
  const volumeTooltipFormatter = (value: number, name: string) => [
    formatThousandVolumeTooltip(Number(value), displayUnitId),
    name,
  ];
  const initialOilRateUnitLabel = displayUnitId === SCENARIO_UNIT_BARRELS ? '1000 bbl' : '1000 t';

  useEffect(() => {
    const loadScenarios = async () => {
      try {
        setLoadingScenarios(true);
        setScenariosError(null);
        const response = await scenariosApi.getScenarios({ limit: 500 });
        setScenarios(response.scenarios);
      } catch (error) {
        console.error(error);
        setScenariosError('Failed to load scenarios list.');
      } finally {
        setLoadingScenarios(false);
      }
    };

    loadScenarios();
  }, []);

  useEffect(() => {
    const selectedIds = [scenarioAId, scenarioBId].filter(Boolean);
    if (selectedIds.length === 0) return;

    const fetchStatuses = async () => {
      const uniqueIds = Array.from(new Set(selectedIds));
      await Promise.all(
        uniqueIds.map(async (scenarioId) => {
          if (statusByScenarioId[scenarioId]) return;
          try {
            const status = await scenariosApi.getCalculationsStatus(scenarioId);
            const reason = status.is_up_to_date
              ? null
              : (!status.calculated_at
                ? 'Not calculated yet.'
                : 'Outdated: scenario inputs changed after last calculation.');
            setStatusByScenarioId((prev) => ({
              ...prev,
              [scenarioId]: { is_up_to_date: status.is_up_to_date, reason },
            }));
          } catch (error) {
            console.error(error);
            setStatusByScenarioId((prev) => ({
              ...prev,
              [scenarioId]: { is_up_to_date: false, reason: 'Unable to verify calculation freshness.' },
            }));
          }
        })
      );
    };

    fetchStatuses();
  }, [scenarioAId, scenarioBId, statusByScenarioId]);

  const statusA = scenarioAId ? statusByScenarioId[scenarioAId] : null;
  const statusB = scenarioBId ? statusByScenarioId[scenarioBId] : null;

  const selectedScenarioA = useMemo(
    () => scenarios.find((scenario) => scenario.id === scenarioAId) ?? null,
    [scenarios, scenarioAId]
  );
  const selectedScenarioB = useMemo(
    () => scenarios.find((scenario) => scenario.id === scenarioBId) ?? null,
    [scenarios, scenarioBId]
  );

  const handleCompare = async () => {
    if (!scenarioAId || !scenarioBId) {
      setCompareError('Select both scenarios before comparison.');
      return;
    }
    if (scenarioAId === scenarioBId) {
      setCompareError('Select two different scenarios.');
      return;
    }
    try {
      setCompareLoading(true);
      setCompareError(null);
      const response = await scenariosApi.compareFieldCurves({
        scenario_ids: [scenarioAId, scenarioBId],
        curve_type: curveType,
      });
      setComparisonSeries(response.scenarios);

      setStatusByScenarioId((prev) => {
        const next = { ...prev };
        response.scenarios.forEach((item) => {
          next[item.scenario_id] = {
            is_up_to_date: item.is_up_to_date,
            reason: item.stale_reason ?? null,
          };
        });
        return next;
      });
    } catch (error: any) {
      console.error(error);
      setComparisonSeries(null);
      setCompareError(error?.response?.data?.detail || 'Failed to compare scenarios.');
    } finally {
      setCompareLoading(false);
    }
  };

  const curvesChartData = useMemo(() => {
    if (!comparisonSeries || comparisonSeries.length !== 2) return [];
    const [first, second] = comparisonSeries;
    const firstMap = new Map(first.points.map((point) => [point.year, point]));
    const secondMap = new Map(second.points.map((point) => [point.year, point]));
    const years = Array.from(new Set([...firstMap.keys(), ...secondMap.keys()])).sort((a, b) => a - b);

    return years.map((year) => {
      const firstPoint = firstMap.get(year);
      const secondPoint = secondMap.get(year);
      const first = convertComparisonOilLiquid(displayUnitId, firstPoint?.oil, firstPoint?.liquid);
      const second = convertComparisonOilLiquid(displayUnitId, secondPoint?.oil, secondPoint?.liquid);
      return {
        year,
        firstOil: first.oil,
        firstLiquid: first.liquid,
        secondOil: second.oil,
        secondLiquid: second.liquid,
      };
    });
  }, [comparisonSeries, displayUnitId]);

  const chartEndYear = useMemo(() => {
    if (!comparisonSeries || comparisonSeries.length !== 2) return null;
    const scenarioEndYears = comparisonSeries.map((series) => {
      const lastPositivePoint = [...series.points]
        .reverse()
        .find((point) => (point.oil ?? 0) > 0 || (point.liquid ?? 0) > 0);
      return lastPositivePoint?.year ?? null;
    });
    const validYears = scenarioEndYears.filter((year): year is number => year !== null);
    return validYears.length > 0 ? Math.max(...validYears) : null;
  }, [comparisonSeries]);

  const truncatedCurvesChartData = useMemo(() => {
    if (chartEndYear === null) return curvesChartData;
    return curvesChartData.filter((row) => row.year <= chartEndYear);
  }, [curvesChartData, chartEndYear]);

  const diffChartData = useMemo(() => {
    if (!comparisonSeries || comparisonSeries.length !== 2) return [];
    const [first, second] = comparisonSeries;
    const firstMap = new Map(first.points.map((point) => [point.year, point]));
    const secondMap = new Map(second.points.map((point) => [point.year, point]));
    const years = Array.from(new Set([...firstMap.keys(), ...secondMap.keys()])).sort((a, b) => a - b);

    return years.map((year) => {
      const firstYear = firstMap.get(year);
      const secondYear = secondMap.get(year);
      const first = convertComparisonOilLiquid(displayUnitId, firstYear?.oil, firstYear?.liquid);
      const second = convertComparisonOilLiquid(displayUnitId, secondYear?.oil, secondYear?.liquid);
      return {
        year,
        diffOil: (first.oil ?? 0) - (second.oil ?? 0),
        diffLiquid: (first.liquid ?? 0) - (second.liquid ?? 0),
      };
    });
  }, [comparisonSeries, displayUnitId]);

  const truncatedDiffChartData = useMemo(() => {
    if (chartEndYear === null) return diffChartData;
    return diffChartData.filter((row) => row.year <= chartEndYear);
  }, [diffChartData, chartEndYear]);

  const totalDiff = useMemo(() => {
    return truncatedDiffChartData.reduce(
      (acc, row) => {
        acc.oil += row.diffOil;
        acc.liquid += row.diffLiquid;
        return acc;
      },
      { oil: 0, liquid: 0 }
    );
  }, [truncatedDiffChartData]);

  const activeWellsChartData = useMemo(() => {
    if (!comparisonSeries || comparisonSeries.length !== 2) return [];
    const [first, second] = comparisonSeries;
    const firstMap = new Map(first.points.map((point) => [point.year, point]));
    const secondMap = new Map(second.points.map((point) => [point.year, point]));
    const years = Array.from(new Set([...firstMap.keys(), ...secondMap.keys()])).sort((a, b) => a - b);

    return years.map((year) => ({
      year,
      firstActiveWells: firstMap.get(year)?.active_wells ?? 0,
      secondActiveWells: secondMap.get(year)?.active_wells ?? 0,
    }));
  }, [comparisonSeries]);

  const truncatedActiveWellsChartData = useMemo(() => {
    if (chartEndYear === null) return activeWellsChartData;
    return activeWellsChartData.filter((row) => row.year <= chartEndYear);
  }, [activeWellsChartData, chartEndYear]);

  const diffActiveWellsChartData = useMemo(() => {
    return truncatedActiveWellsChartData.map((row) => ({
      year: row.year,
      diffActiveWells: row.firstActiveWells - row.secondActiveWells,
    }));
  }, [truncatedActiveWellsChartData]);

  useEffect(() => {
    if (!comparisonSeries || comparisonSeries.length !== 2) {
      setLayerComparisonSections([]);
      return;
    }
    const scenario1Layers = comparisonSeries[0].layers ?? [];
    const scenario2Layers = comparisonSeries[1].layers ?? [];
    if (scenario1Layers.length === 0 || scenario2Layers.length === 0) {
      setLayerComparisonSections([]);
      return;
    }
    setLayerComparisonSections([
      {
        id: `layer-section-${Date.now()}`,
        scenario1LayerId: scenario1Layers[0].scenario_layer_id,
        scenario2LayerId: scenario2Layers[0].scenario_layer_id,
      },
    ]);
  }, [comparisonSeries]);

  const addLayerComparisonSection = () => {
    if (!comparisonSeries || comparisonSeries.length !== 2) return;
    const scenario1Layers = comparisonSeries[0].layers ?? [];
    const scenario2Layers = comparisonSeries[1].layers ?? [];
    if (scenario1Layers.length === 0 || scenario2Layers.length === 0) return;
    setLayerComparisonSections((prev) => [
      ...prev,
      {
        id: `layer-section-${Date.now()}-${prev.length + 1}`,
        scenario1LayerId: scenario1Layers[0].scenario_layer_id,
        scenario2LayerId: scenario2Layers[0].scenario_layer_id,
      },
    ]);
  };

  const updateLayerSection = (
    sectionId: string,
    patch: Partial<Pick<LayerComparisonSection, 'scenario1LayerId' | 'scenario2LayerId'>>
  ) => {
    setLayerComparisonSections((prev) =>
      prev.map((section) => (section.id === sectionId ? { ...section, ...patch } : section))
    );
  };

  const removeLayerSection = (sectionId: string) => {
    setLayerComparisonSections((prev) => prev.filter((section) => section.id !== sectionId));
  };

  const getMenuLabel = (scenario: ScenarioResponse) => {
    const status = statusByScenarioId[scenario.id];
    if (status && !status.is_up_to_date) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon fontSize="small" color="warning" />
          <span>{scenario.name}</span>
        </Box>
      );
    }
    return scenario.name;
  };

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4">Scenario Comparison</Typography>
        <Typography color="text.secondary">
          Compare two scenarios on field-level yearly productivity curves.
        </Typography>
        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ mb: 0.5, fontWeight: 600 }}>
            Production units
          </FormLabel>
          <RadioGroup
            row
            value={String(displayUnitId)}
            onChange={(event) => setDisplayUnitId(Number(event.target.value))}
          >
            <FormControlLabel value={String(SCENARIO_UNIT_TONS)} control={<Radio size="small" />} label="Tons" />
            <FormControlLabel value={String(SCENARIO_UNIT_BARRELS)} control={<Radio size="small" />} label="Barrels" />
          </RadioGroup>
        </FormControl>
      </Stack>

      {scenariosError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {scenariosError}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Scenario 1</InputLabel>
              <Select
                value={scenarioAId}
                label="Scenario 1"
                onChange={(event) => setScenarioAId(event.target.value)}
                disabled={loadingScenarios}
              >
                {scenarios.map((scenario) => (
                  <MenuItem key={scenario.id} value={scenario.id}>
                    {getMenuLabel(scenario)}
                  </MenuItem>
                ))}
              </Select>
              {statusA && !statusA.is_up_to_date && (
                <FormHelperText sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningAmberIcon sx={{ fontSize: 14 }} />
                  {statusA.reason || 'Scenario is outdated and should be recalculated.'}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Scenario 2</InputLabel>
              <Select
                value={scenarioBId}
                label="Scenario 2"
                onChange={(event) => setScenarioBId(event.target.value)}
                disabled={loadingScenarios}
              >
                {scenarios.map((scenario) => (
                  <MenuItem key={scenario.id} value={scenario.id}>
                    {getMenuLabel(scenario)}
                  </MenuItem>
                ))}
              </Select>
              {statusB && !statusB.is_up_to_date && (
                <FormHelperText sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningAmberIcon sx={{ fontSize: 14 }} />
                  {statusB.reason || 'Scenario is outdated and should be recalculated.'}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Calculation variant</InputLabel>
              <Select
                value={curveType}
                label="Calculation variant"
                onChange={(event) => setCurveType(event.target.value as ScenarioCurveType)}
              >
                <MenuItem value="ml">ML</MenuItem>
                <MenuItem value="p10">P10</MenuItem>
                <MenuItem value="p50">P50</MenuItem>
                <MenuItem value="p90">P90</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={8}>
            <Stack direction="row" justifyContent={{ xs: 'stretch', md: 'flex-start' }}>
              <Button
                variant="contained"
                onClick={handleCompare}
                disabled={compareLoading || !scenarioAId || !scenarioBId}
                startIcon={compareLoading ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {compareLoading ? 'Comparing...' : 'Compare'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {compareError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {compareError}
        </Alert>
      )}

      {comparisonSeries && comparisonSeries.length === 2 && (
        <>
          <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`${selectedScenarioA?.name || 'Scenario 1'} (Scenario 1)`} color="primary" />
            <Chip label={`${selectedScenarioB?.name || 'Scenario 2'} (Scenario 2)`} color="secondary" />
            <Chip label={`Curve: ${curveType.toUpperCase()}`} color="info" />
          </Stack>

          {comparisonSeries.some((series) => !series.is_up_to_date) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              One or both scenarios are outdated. Recalculate them for a consistent comparison.
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: CHART_CARD_HEIGHT, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Field productivity curves by year
                </Typography>
                <Box sx={{ width: '100%', height: CHART_HEIGHT, flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={truncatedCurvesChartData}
                      margin={{ top: 10, right: 16, left: 16, bottom: 28 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year">
                        <Label value="Year" position="bottom" style={{ textAnchor: 'middle' }} />
                      </XAxis>
                      <YAxis>
                        <Label
                          value={productionAxisLabel}
                          angle={-90}
                          position="left"
                          style={{ textAnchor: 'middle' }}
                        />
                      </YAxis>
                      <Tooltip
                        formatter={volumeTooltipFormatter}
                        labelFormatter={(label) => `Year: ${label}`}
                      />
                      <Line type="monotone" dataKey="firstOil" name="Scenario 1 Oil" stroke="#1976d2" strokeWidth={2} connectNulls={false} />
                      <Line type="monotone" dataKey="firstLiquid" name="Scenario 1 Liquid" stroke="#63a4ff" strokeWidth={2} connectNulls={false} />
                      <Line type="monotone" dataKey="secondOil" name="Scenario 2 Oil" stroke="#9c27b0" strokeWidth={2} connectNulls={false} />
                      <Line type="monotone" dataKey="secondLiquid" name="Scenario 2 Liquid" stroke="#ce93d8" strokeWidth={2} connectNulls={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ mt: 1.25, minHeight: 56 }}>
                  {renderResponsiveLegend({
                    payload: [
                      { value: 'Scenario 1 Oil', color: '#1976d2' },
                      { value: 'Scenario 1 Liquid', color: '#63a4ff' },
                      { value: 'Scenario 2 Oil', color: '#9c27b0' },
                      { value: 'Scenario 2 Liquid', color: '#ce93d8' },
                    ],
                  })}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: CHART_CARD_HEIGHT, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Production difference (Scenario 1 - Scenario 2)
                </Typography>
                <Box sx={{ width: '100%', height: CHART_HEIGHT, position: 'relative', flexShrink: 0 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 2,
                      maxWidth: { xs: '80%', sm: '55%' },
                      pointerEvents: 'none',
                    }}
                  >
                    <MuiPaper
                      elevation={3}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        minWidth: 210,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                        Diff (Scenario 1 - Scenario 2)
                      </Typography>
                      <Typography variant="body2">
                        Oil:{' '}
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: totalDiff.oil >= 0 ? 'success.main' : 'error.main' }}
                        >
                          {totalDiff.oil >= 0 ? '+' : ''}
                          {totalDiff.oil.toFixed(1)}
                        </Box>
                      </Typography>
                      <Typography variant="body2">
                        Liquid:{' '}
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: totalDiff.liquid >= 0 ? 'success.main' : 'error.main' }}
                        >
                          {totalDiff.liquid >= 0 ? '+' : ''}
                          {totalDiff.liquid.toFixed(1)}
                        </Box>
                      </Typography>
                    </MuiPaper>
                  </Box>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={truncatedDiffChartData}
                      margin={{ top: 10, right: 16, left: 16, bottom: 28 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year">
                        <Label value="Year" position="bottom" style={{ textAnchor: 'middle' }} />
                      </XAxis>
                      <YAxis>
                        <Label
                          value={differenceAxisLabel}
                          angle={-90}
                          position="left"
                          style={{ textAnchor: 'middle' }}
                        />
                      </YAxis>
                      <Tooltip
                        formatter={volumeTooltipFormatter}
                        labelFormatter={(label) => `Year: ${label}`}
                      />
                      <Line type="monotone" dataKey="diffOil" name="Oil difference" stroke="#ef6c00" strokeWidth={2} />
                      <Line type="monotone" dataKey="diffLiquid" name="Liquid difference" stroke="#2e7d32" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ mt: 1.25, minHeight: 56 }}>
                  {renderResponsiveLegend({
                    payload: [
                      { value: 'Oil difference', color: '#ef6c00' },
                      { value: 'Liquid difference', color: '#2e7d32' },
                    ],
                  })}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: CHART_CARD_HEIGHT, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Active wells by year
                </Typography>
                <Box sx={{ width: '100%', height: CHART_HEIGHT, flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={truncatedActiveWellsChartData}
                      margin={{ top: 10, right: 16, left: 16, bottom: 28 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year">
                        <Label value="Year" position="bottom" style={{ textAnchor: 'middle' }} />
                      </XAxis>
                      <YAxis allowDecimals={false}>
                        <Label
                          value="Active wells (count)"
                          angle={-90}
                          position="left"
                          style={{ textAnchor: 'middle' }}
                        />
                      </YAxis>
                      <Tooltip
                        formatter={(value: number, name: string) => [`${Math.round(Number(value))} wells`, name]}
                        labelFormatter={(label) => `Year: ${label}`}
                      />
                      <Bar dataKey="firstActiveWells" name="Scenario 1 Active Wells" fill="#00695c" />
                      <Bar dataKey="secondActiveWells" name="Scenario 2 Active Wells" fill="#8e24aa" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ mt: 1.25, minHeight: 56 }}>
                  {renderResponsiveLegend({
                    payload: [
                      { value: 'Scenario 1 Active Wells', color: '#00695c' },
                      { value: 'Scenario 2 Active Wells', color: '#8e24aa' },
                    ],
                  })}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: CHART_CARD_HEIGHT, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Active wells difference (Scenario 1 - Scenario 2)
                </Typography>
                <Box sx={{ width: '100%', height: CHART_HEIGHT, flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={diffActiveWellsChartData}
                      margin={{ top: 10, right: 16, left: 16, bottom: 28 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year">
                        <Label value="Year" position="bottom" style={{ textAnchor: 'middle' }} />
                      </XAxis>
                      <YAxis allowDecimals={false}>
                        <Label
                          value="Difference (wells)"
                          angle={-90}
                          position="left"
                          style={{ textAnchor: 'middle' }}
                        />
                      </YAxis>
                      <Tooltip
                        formatter={(value: number, name: string) => [`${Math.round(Number(value))} wells`, name]}
                        labelFormatter={(label) => `Year: ${label}`}
                      />
                      <Bar dataKey="diffActiveWells" name="Active wells difference" fill="#5d4037" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ mt: 1.25, minHeight: 56 }}>
                  {renderResponsiveLegend({
                    payload: [{ value: 'Active wells difference', color: '#5d4037' }],
                  })}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h5">Reservoir comparison sections</Typography>
            </Stack>

            {layerComparisonSections.length === 0 && (
              <Alert severity="info">
                No reservoir comparison sections yet.
              </Alert>
            )}

            {layerComparisonSections.map((section, index) => {
              const scenario1Layers = comparisonSeries[0].layers ?? [];
              const scenario2Layers = comparisonSeries[1].layers ?? [];
              const scenario1Layer = scenario1Layers.find((item) => item.scenario_layer_id === section.scenario1LayerId) ?? null;
              const scenario2Layer = scenario2Layers.find((item) => item.scenario_layer_id === section.scenario2LayerId) ?? null;

              const layerProductionCurvesData = (() => {
                if (!scenario1Layer || !scenario2Layer) return [];
                const firstMap = new Map(scenario1Layer.points.map((point) => [point.year, point]));
                const secondMap = new Map(scenario2Layer.points.map((point) => [point.year, point]));
                const years = Array.from(new Set([...firstMap.keys(), ...secondMap.keys()])).sort((a, b) => a - b);
                return years.map((year) => {
                  const firstPoint = firstMap.get(year);
                  const secondPoint = secondMap.get(year);
                  const first = convertComparisonOilLiquid(displayUnitId, firstPoint?.oil, firstPoint?.liquid);
                  const second = convertComparisonOilLiquid(displayUnitId, secondPoint?.oil, secondPoint?.liquid);
                  return {
                    year,
                    firstOil: first.oil,
                    firstLiquid: first.liquid,
                    secondOil: second.oil,
                    secondLiquid: second.liquid,
                  };
                });
              })();

              const layerChartEndYear = (() => {
                if (!scenario1Layer || !scenario2Layer) return null;
                const s1Last = [...scenario1Layer.points]
                  .reverse()
                  .find((point) => point.oil > 0 || point.liquid > 0)?.year ?? null;
                const s2Last = [...scenario2Layer.points]
                  .reverse()
                  .find((point) => point.oil > 0 || point.liquid > 0)?.year ?? null;
                const years = [s1Last, s2Last].filter((year): year is number => year !== null);
                return years.length ? Math.max(...years) : null;
              })();

              const truncatedLayerProductionCurvesData = layerChartEndYear === null
                ? layerProductionCurvesData
                : layerProductionCurvesData.filter((row) => row.year <= layerChartEndYear);

              const layerProductionDiffData = truncatedLayerProductionCurvesData.map((row) => ({
                year: row.year,
                diffOil: (row.firstOil ?? 0) - (row.secondOil ?? 0),
                diffLiquid: (row.firstLiquid ?? 0) - (row.secondLiquid ?? 0),
              }));

              const layerActiveWellsData = (() => {
                if (!scenario1Layer || !scenario2Layer) return [];
                const firstMap = new Map(scenario1Layer.points.map((point) => [point.year, point]));
                const secondMap = new Map(scenario2Layer.points.map((point) => [point.year, point]));
                const years = Array.from(new Set([...firstMap.keys(), ...secondMap.keys()])).sort((a, b) => a - b);
                const rows = years.map((year) => ({
                  year,
                  firstActiveWells: firstMap.get(year)?.active_wells ?? 0,
                  secondActiveWells: secondMap.get(year)?.active_wells ?? 0,
                }));
                return layerChartEndYear === null ? rows : rows.filter((row) => row.year <= layerChartEndYear);
              })();

              const layerActiveWellsDiffData = layerActiveWellsData.map((row) => ({
                year: row.year,
                diffActiveWells: row.firstActiveWells - row.secondActiveWells,
              }));

              const layerTotalDiff = layerProductionDiffData.reduce(
                (acc, row) => ({ oil: acc.oil + row.diffOil, liquid: acc.liquid + row.diffLiquid }),
                { oil: 0, liquid: 0 }
              );

              const baseWellCurvesData = (() => {
                if (!scenario1Layer || !scenario2Layer) return [];
                const firstMap = new Map((scenario1Layer.base_points ?? []).map((point) => [point.year_index, point]));
                const secondMap = new Map((scenario2Layer.base_points ?? []).map((point) => [point.year_index, point]));
                const years = Array.from(new Set([...firstMap.keys(), ...secondMap.keys()])).sort((a, b) => a - b);
                return years.map((yearIndex) => {
                  const firstPoint = firstMap.get(yearIndex);
                  const secondPoint = secondMap.get(yearIndex);
                  const first = convertComparisonOilLiquid(displayUnitId, firstPoint?.oil, firstPoint?.liquid);
                  const second = convertComparisonOilLiquid(displayUnitId, secondPoint?.oil, secondPoint?.liquid);
                  return {
                    yearIndex,
                    firstOil: first.oil,
                    firstLiquid: first.liquid,
                    secondOil: second.oil,
                    secondLiquid: second.liquid,
                  };
                });
              })();

              const baseWellDiffData = baseWellCurvesData.map((row) => ({
                yearIndex: row.yearIndex,
                diffOil: (row.firstOil ?? 0) - (row.secondOil ?? 0),
                diffLiquid: (row.firstLiquid ?? 0) - (row.secondLiquid ?? 0),
              }));

              const baseWellTotalDiff = baseWellDiffData.reduce(
                (acc, row) => ({ oil: acc.oil + row.diffOil, liquid: acc.liquid + row.diffLiquid }),
                { oil: 0, liquid: 0 }
              );

              const kinScenario1 = scenario1Layer?.recovery_factor;
              const kinScenario2 = scenario2Layer?.recovery_factor;
              const initialOilScenario1 = convertOilVolumeForDisplay(
                displayUnitId,
                scenario1Layer?.initial_oil_rate ?? 0,
              );
              const initialOilScenario2 = convertOilVolumeForDisplay(
                displayUnitId,
                scenario2Layer?.initial_oil_rate ?? 0,
              );

              return (
                <Paper key={section.id} sx={{ p: 2, mb: 3 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6">Reservoir section #{index + 1}</Typography>
                    <IconButton
                      aria-label="remove layer comparison section"
                      onClick={() => removeLayerSection(section.id)}
                      disabled={layerComparisonSections.length <= 1}
                      size="small"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Reservoir in Scenario 1</InputLabel>
                        <Select
                          value={section.scenario1LayerId}
                          label="Reservoir in Scenario 1"
                          onChange={(event) => updateLayerSection(section.id, { scenario1LayerId: event.target.value })}
                        >
                          {scenario1Layers.map((layer) => (
                            <MenuItem key={layer.scenario_layer_id} value={layer.scenario_layer_id}>
                              {layer.layer_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Reservoir in Scenario 2</InputLabel>
                        <Select
                          value={section.scenario2LayerId}
                          label="Reservoir in Scenario 2"
                          onChange={(event) => updateLayerSection(section.id, { scenario2LayerId: event.target.value })}
                        >
                          {scenario2Layers.map((layer) => (
                            <MenuItem key={layer.scenario_layer_id} value={layer.scenario_layer_id}>
                              {layer.layer_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {!scenario1Layer || !scenario2Layer ? (
                    <Alert severity="warning">Selected reservoirs could not be loaded.</Alert>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <MuiPaper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Recovery factor (selected reservoirs)
                          </Typography>
                          <Typography variant="body2">
                            Scenario 1:{' '}
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              {kinScenario1 !== null && kinScenario1 !== undefined ? kinScenario1.toFixed(3) : 'N/A'}
                            </Box>
                          </Typography>
                          <Typography variant="body2">
                            Scenario 2:{' '}
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              {kinScenario2 !== null && kinScenario2 !== undefined ? kinScenario2.toFixed(3) : 'N/A'}
                            </Box>
                          </Typography>
                        </MuiPaper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MuiPaper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Initial oil rate by scenarios ({initialOilRateUnitLabel})
                          </Typography>
                          <Typography variant="body2">
                            Scenario 1:{' '}
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              {initialOilScenario1.toFixed(2)}
                            </Box>
                          </Typography>
                          <Typography variant="body2">
                            Scenario 2:{' '}
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              {initialOilScenario2.toFixed(2)}
                            </Box>
                          </Typography>
                        </MuiPaper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: CHART_CARD_HEIGHT, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Base well comparison (oil and liquid)
                          </Typography>
                          <Box sx={{ width: '100%', height: CHART_HEIGHT, flexShrink: 0 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={baseWellCurvesData} margin={{ top: 10, right: 16, left: 16, bottom: 28 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="yearIndex">
                                  <Label value="Year index" position="bottom" style={{ textAnchor: 'middle' }} />
                                </XAxis>
                                <YAxis>
                                  <Label value={productionAxisLabel} angle={-90} position="left" style={{ textAnchor: 'middle' }} />
                                </YAxis>
                                <Tooltip
                                  formatter={volumeTooltipFormatter}
                                  labelFormatter={(label) => `Year index: ${label}`}
                                />
                                <Line type="monotone" dataKey="firstOil" name="Scenario 1 Oil" stroke="#1565c0" strokeWidth={2} connectNulls={false} />
                                <Line type="monotone" dataKey="firstLiquid" name="Scenario 1 Liquid" stroke="#42a5f5" strokeWidth={2} connectNulls={false} />
                                <Line type="monotone" dataKey="secondOil" name="Scenario 2 Oil" stroke="#6a1b9a" strokeWidth={2} connectNulls={false} />
                                <Line type="monotone" dataKey="secondLiquid" name="Scenario 2 Liquid" stroke="#ba68c8" strokeWidth={2} connectNulls={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box sx={{ mt: 1.25, minHeight: 56 }}>
                            {renderResponsiveLegend({
                              payload: [
                                { value: 'Scenario 1 Oil', color: '#1565c0' },
                                { value: 'Scenario 1 Liquid', color: '#42a5f5' },
                                { value: 'Scenario 2 Oil', color: '#6a1b9a' },
                                { value: 'Scenario 2 Liquid', color: '#ba68c8' },
                              ],
                            })}
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: CHART_CARD_HEIGHT, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Base well difference (Scenario 1 - Scenario 2)
                          </Typography>
                          <Box sx={{ width: '100%', height: CHART_HEIGHT, position: 'relative', flexShrink: 0 }}>
                            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, maxWidth: { xs: '80%', sm: '55%' }, pointerEvents: 'none' }}>
                              <MuiPaper elevation={3} sx={{ px: 1.5, py: 0.75, border: 1, borderColor: 'divider', bgcolor: 'background.paper', minWidth: 210 }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                                  Base well diff (S1 - S2)
                                </Typography>
                                <Typography variant="body2">
                                  Oil:{' '}
                                  <Box component="span" sx={{ fontWeight: 700, color: baseWellTotalDiff.oil >= 0 ? 'success.main' : 'error.main' }}>
                                    {baseWellTotalDiff.oil >= 0 ? '+' : ''}
                                    {baseWellTotalDiff.oil.toFixed(1)}
                                  </Box>
                                </Typography>
                                <Typography variant="body2">
                                  Liquid:{' '}
                                  <Box component="span" sx={{ fontWeight: 700, color: baseWellTotalDiff.liquid >= 0 ? 'success.main' : 'error.main' }}>
                                    {baseWellTotalDiff.liquid >= 0 ? '+' : ''}
                                    {baseWellTotalDiff.liquid.toFixed(1)}
                                  </Box>
                                </Typography>
                              </MuiPaper>
                            </Box>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={baseWellDiffData} margin={{ top: 10, right: 16, left: 16, bottom: 28 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="yearIndex">
                                  <Label value="Year index" position="bottom" style={{ textAnchor: 'middle' }} />
                                </XAxis>
                                <YAxis>
                                  <Label value={differenceAxisLabel} angle={-90} position="left" style={{ textAnchor: 'middle' }} />
                                </YAxis>
                                <Tooltip
                                  formatter={volumeTooltipFormatter}
                                  labelFormatter={(label) => `Year index: ${label}`}
                                />
                                <Line type="monotone" dataKey="diffOil" name="Oil difference" stroke="#ef6c00" strokeWidth={2} />
                                <Line type="monotone" dataKey="diffLiquid" name="Liquid difference" stroke="#2e7d32" strokeWidth={2} />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box sx={{ mt: 1.25, minHeight: 56 }}>
                            {renderResponsiveLegend({
                              payload: [
                                { value: 'Oil difference', color: '#ef6c00' },
                                { value: 'Liquid difference', color: '#2e7d32' },
                              ],
                            })}
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: CHART_CARD_HEIGHT, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Reservoir productivity curves
                          </Typography>
                          <Box sx={{ width: '100%', height: CHART_HEIGHT, flexShrink: 0 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={truncatedLayerProductionCurvesData} margin={{ top: 10, right: 16, left: 16, bottom: 28 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="year">
                                  <Label value="Year" position="bottom" style={{ textAnchor: 'middle' }} />
                                </XAxis>
                                <YAxis>
                                  <Label value={productionAxisLabel} angle={-90} position="left" style={{ textAnchor: 'middle' }} />
                                </YAxis>
                                <Tooltip
                                  formatter={volumeTooltipFormatter}
                                  labelFormatter={(label) => `Year: ${label}`}
                                />
                                <Line type="monotone" dataKey="firstOil" name="Scenario 1 Oil" stroke="#1976d2" strokeWidth={2} connectNulls={false} />
                                <Line type="monotone" dataKey="firstLiquid" name="Scenario 1 Liquid" stroke="#63a4ff" strokeWidth={2} connectNulls={false} />
                                <Line type="monotone" dataKey="secondOil" name="Scenario 2 Oil" stroke="#9c27b0" strokeWidth={2} connectNulls={false} />
                                <Line type="monotone" dataKey="secondLiquid" name="Scenario 2 Liquid" stroke="#ce93d8" strokeWidth={2} connectNulls={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box sx={{ mt: 1.25, minHeight: 56 }}>
                            {renderResponsiveLegend({
                              payload: [
                                { value: 'Scenario 1 Oil', color: '#1976d2' },
                                { value: 'Scenario 1 Liquid', color: '#63a4ff' },
                                { value: 'Scenario 2 Oil', color: '#9c27b0' },
                                { value: 'Scenario 2 Liquid', color: '#ce93d8' },
                              ],
                            })}
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: CHART_CARD_HEIGHT, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Reservoir production difference (Scenario 1 - Scenario 2)
                          </Typography>
                          <Box sx={{ width: '100%', height: CHART_HEIGHT, position: 'relative', flexShrink: 0 }}>
                            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, maxWidth: { xs: '80%', sm: '55%' }, pointerEvents: 'none' }}>
                              <MuiPaper elevation={3} sx={{ px: 1.5, py: 0.75, border: 1, borderColor: 'divider', bgcolor: 'background.paper', minWidth: 210 }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                                  Diff (Scenario 1 - Scenario 2)
                                </Typography>
                                <Typography variant="body2">
                                  Oil:{' '}
                                  <Box component="span" sx={{ fontWeight: 700, color: layerTotalDiff.oil >= 0 ? 'success.main' : 'error.main' }}>
                                    {layerTotalDiff.oil >= 0 ? '+' : ''}
                                    {layerTotalDiff.oil.toFixed(1)}
                                  </Box>
                                </Typography>
                                <Typography variant="body2">
                                  Liquid:{' '}
                                  <Box component="span" sx={{ fontWeight: 700, color: layerTotalDiff.liquid >= 0 ? 'success.main' : 'error.main' }}>
                                    {layerTotalDiff.liquid >= 0 ? '+' : ''}
                                    {layerTotalDiff.liquid.toFixed(1)}
                                  </Box>
                                </Typography>
                              </MuiPaper>
                            </Box>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={layerProductionDiffData} margin={{ top: 10, right: 16, left: 16, bottom: 28 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="year">
                                  <Label value="Year" position="bottom" style={{ textAnchor: 'middle' }} />
                                </XAxis>
                                <YAxis>
                                  <Label value={differenceAxisLabel} angle={-90} position="left" style={{ textAnchor: 'middle' }} />
                                </YAxis>
                                <Tooltip
                                  formatter={volumeTooltipFormatter}
                                  labelFormatter={(label) => `Year: ${label}`}
                                />
                                <Line type="monotone" dataKey="diffOil" name="Oil difference" stroke="#ef6c00" strokeWidth={2} />
                                <Line type="monotone" dataKey="diffLiquid" name="Liquid difference" stroke="#2e7d32" strokeWidth={2} />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box sx={{ mt: 1.25, minHeight: 56 }}>
                            {renderResponsiveLegend({
                              payload: [
                                { value: 'Oil difference', color: '#ef6c00' },
                                { value: 'Liquid difference', color: '#2e7d32' },
                              ],
                            })}
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: CHART_CARD_HEIGHT, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Reservoir active wells by year
                          </Typography>
                          <Box sx={{ width: '100%', height: CHART_HEIGHT, flexShrink: 0 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={layerActiveWellsData} margin={{ top: 10, right: 16, left: 16, bottom: 28 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="year">
                                  <Label value="Year" position="bottom" style={{ textAnchor: 'middle' }} />
                                </XAxis>
                                <YAxis allowDecimals={false}>
                                  <Label value="Active wells (count)" angle={-90} position="left" style={{ textAnchor: 'middle' }} />
                                </YAxis>
                                <Tooltip
                                  formatter={(value: number, name: string) => [`${Math.round(Number(value))} wells`, name]}
                                  labelFormatter={(label) => `Year: ${label}`}
                                />
                                <Bar dataKey="firstActiveWells" name="Scenario 1 Active Wells" fill="#00695c" />
                                <Bar dataKey="secondActiveWells" name="Scenario 2 Active Wells" fill="#8e24aa" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box sx={{ mt: 1.25, minHeight: 56 }}>
                            {renderResponsiveLegend({
                              payload: [
                                { value: 'Scenario 1 Active Wells', color: '#00695c' },
                                { value: 'Scenario 2 Active Wells', color: '#8e24aa' },
                              ],
                            })}
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: CHART_CARD_HEIGHT, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Reservoir active wells difference (Scenario 1 - Scenario 2)
                          </Typography>
                          <Box sx={{ width: '100%', height: CHART_HEIGHT, flexShrink: 0 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={layerActiveWellsDiffData} margin={{ top: 10, right: 16, left: 16, bottom: 28 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="year">
                                  <Label value="Year" position="bottom" style={{ textAnchor: 'middle' }} />
                                </XAxis>
                                <YAxis allowDecimals={false}>
                                  <Label value="Difference (wells)" angle={-90} position="left" style={{ textAnchor: 'middle' }} />
                                </YAxis>
                                <Tooltip
                                  formatter={(value: number, name: string) => [`${Math.round(Number(value))} wells`, name]}
                                  labelFormatter={(label) => `Year: ${label}`}
                                />
                                <Bar dataKey="diffActiveWells" name="Active wells difference" fill="#5d4037" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box sx={{ mt: 1.25, minHeight: 56 }}>
                            {renderResponsiveLegend({
                              payload: [{ value: 'Active wells difference', color: '#5d4037' }],
                            })}
                          </Box>
                        </Paper>
                      </Grid>

                    </Grid>
                  )}
                </Paper>
              );
            })}

            {comparisonSeries && comparisonSeries.length === 2 && layerComparisonSections.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addLayerComparisonSection}>
                  Add reservoir comparison section
                </Button>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};
